import omitDeepLodash from "omit-deep-lodash";
import { MutationTree, GetterTree, ActionTree } from "vuex";
import {} from "vuefront-api";
import { RootState } from "vuefront-store";
import {
  getGraphQLErrors,
  shouldInvalidateApolloCache,
} from "./apollo.helpers";
import {
  classifyGraphQLPartialData,
  reportGraphQLPartialData,
  type GraphQLPartialDataPolicy,
} from "../../../utils/graphqlPartialData";
import {
  sendMutationWithProductMedia,
  sendWithProductMedia,
} from "../../../utils/productMedia";

/**
 * Opt-in `partialData` option of `apollo/query` and `apollo/mutate` (never
 * forwarded to Apollo): applies the tiers of utils/graphqlPartialData to a
 * response with field errors. A usable response continues with the cleaned
 * data and no errors; anything else takes the unchanged failure path.
 */
const applyPartialDataPolicy = (
  res: any,
  errors: unknown[] | null,
  policy: GraphQLPartialDataPolicy | undefined
): { res: any; errors: unknown[] | null } => {
  if (!policy || typeof policy !== "object") return { res, errors };
  const outcome = classifyGraphQLPartialData(errors, res?.data ?? null, policy);
  if (!outcome.ok) return { res, errors };
  reportGraphQLPartialData(outcome.issues, policy.operation);
  if (outcome.issues.length || outcome.droppedItems) {
    try {
      policy.onPartialData?.({
        issues: outcome.issues,
        droppedByList: outcome.droppedByList,
        droppedItems: outcome.droppedItems,
      });
    } catch {
      // A caller's bookkeeping must never turn usable data into a failure.
    }
  }
  return {
    res: { ...res, data: outcome.data, errors: undefined },
    errors: null,
  };
};

export type State = {
  data: Record<string, unknown>;
  prefetchData: Record<string, unknown>;
  keyedData: Record<string, unknown>;
};
export const state = (): State => ({
  data: {},
  prefetchData: {},
  keyedData: {},
});

export const getters: GetterTree<State, RootState> = {
  get(state) {
    return state.data;
  },
  prefetchData(state) {
    return state.prefetchData;
  },
  getByKey: (state) => (key: string) => state.keyedData[key],
};

/**
 * Typed accessor for the legacy shared response bucket. Callers that know
 * the operation's response shape can read it without `any`:
 *
 *   const payload = getMutationResult<AddToCartData["addToCart"]>(
 *     rootGetters, "addToCart"
 *   );
 */
export const getMutationResult = <T>(
  rootGetters: any,
  field: string
): T | undefined => {
  const data = rootGetters["apollo/get"] as Record<string, unknown>;
  return data?.[field] as T | undefined;
};

export const mutations: MutationTree<State> = {
  setData(state, payload) {
    state.data = { ...state.data, ...payload.data };
  },
  setPrefetchData(state, { key, data }) {
    state.prefetchData[key] = data;
  },
  setKeyedData(state, { key, data }) {
    state.keyedData = { ...state.keyedData, [key]: data };
  },
};

export const actions: ActionTree<State, RootState> = {
  async query({ commit }, options) {
    const throwOnError = options?.throwOnError === true;
    try {
      // `key` and `partialData` are consumed here only — they must not be
      // forwarded to Apollo.
      const {
        key,
        invalidateCache: _invalidateCache,
        throwOnError: _throwOnError,
        partialData,
        productMedia,
        ...apolloOptions
      } = options || {};
      const variables = omitDeepLodash(apolloOptions.variables, "__typename");
      const send = (query: any) =>
        this.$vfapollo.query({
          ...apolloOptions,
          query,
          // With errorPolicy "all" Apollo 2.6 writes data that came with
          // errors into its normalized cache, and a later cache-first read
          // returns it as a clean answer. A caller that tolerates field
          // errors gets such data by design, so its responses never touch
          // the cache.
          ...(partialData ? { fetchPolicy: "no-cache" } : {}),
          variables,
          errorPolicy: "all",
        });
      // `media` is selected only while the schema has it (utils/
      // productMedia). If the request with `media` is refused for that
      // field, or fails without a readable answer (the live connector dies
      // with a var_dump on execution errors), the gate closes and the same
      // request goes out once more without the field, so the page gets what
      // 118e12a would have got.
      let res =
        productMedia === true
          ? await sendWithProductMedia(
              this.$vfapollo,
              apolloOptions.query,
              send,
              getGraphQLErrors
            )
          : await send(apolloOptions.query);

      res = omitDeepLodash(res, "__typename");
      let errors = getGraphQLErrors(res);
      ({ res, errors } = applyPartialDataPolicy(res, errors, partialData));

      if (errors) {
        commit("vuefront/setError", errors, { root: true });
        if (throwOnError) {
          const graphQLErrors = Array.isArray(errors) ? errors : [errors];
          const error: any = new Error(
            graphQLErrors
              .map((item: any) => String(item?.message || item || ""))
              .filter(Boolean)
              .join("; ") || "GraphQL query failed"
          );
          error.graphQLErrors = graphQLErrors;
          throw error;
        }
        return null;
      }

      commit("vuefront/setError", false, { root: true });

      // Always keep the legacy shared `data` object working for the ~25
      // components that still read `apollo/get`.
      commit("setData", res);

      const data = res?.data ?? {};
      // Optional keyed storage: lets a caller read its own result back via
      // `apollo/getByKey` without it being clobbered by a concurrent query.
      if (key) {
        commit("setKeyedData", { key, data });
      }

      // Return the result so callers can use their OWN data instead of the
      // shared getter — this is what removes the concurrent-query race that
      // the catalog/product pages previously guarded against by hand.
      return data as Record<string, unknown>;
    } catch (e) {
      commit("vuefront/setResponseError", e, {
        root: true,
      });
      if (throwOnError) throw e;
      return null;
    }
  },
  async mutate({ commit }, options) {
    try {
      const { invalidateCache, partialData, productMedia, ...mutationOptions } =
        options || {};
      const variables = omitDeepLodash(mutationOptions.variables, "__typename");
      const send = (mutation: any) =>
        this.$vfapollo.mutate({
          ...mutationOptions,
          mutation,
          variables,
          errorPolicy: "all",
        });
      // A result that selects `media` goes through the mutation gate
      // (utils/productMedia): without the field while the schema lacks it,
      // and resent without it only after a validation refusal for `media`,
      // never after any other failure (the mutation may have run).
      let res =
        productMedia === true
          ? await sendMutationWithProductMedia(
              this.$vfapollo,
              mutationOptions.mutation,
              send,
              getGraphQLErrors
            )
          : await send(mutationOptions.mutation);
      let errors = getGraphQLErrors(res);
      ({ res, errors } = applyPartialDataPolicy(res, errors, partialData));

      if (errors) {
        commit("vuefront/setError", errors, {
          root: true,
        });
        return null;
      }

      commit("vuefront/setError", false, { root: true });
      commit("setData", res);

      if (shouldInvalidateApolloCache({ invalidateCache })) {
        await this.$vfapollo.clearStore();
      }

      return res?.data ?? null;
    } catch (e) {
      commit("vuefront/setResponseError", e, {
        root: true,
      });
      return null;
    }
  },
  async upload(
    { commit },
    options: { variables: { [key: string]: any }; mutation: any }
  ) {
    try {
      const variables: { [key: string]: any } = omitDeepLodash(
        options.variables,
        "__typename"
      );

      const o = {
        query: options.mutation.loc.source.body,
        variables,
      };

      const map: { [key: number]: string } = {
        0: "file",
      };

      let i: number = 0;
      const fd = new FormData();
      fd.append("operations", JSON.stringify(o));
      for (const key in variables) {
        if (
          typeof variables[key] === "object" &&
          variables[key] instanceof File
        ) {
          map[i] = key;
          fd.append(i.toString(), variables[key]);
          i++;
        }
      }

      fd.append("map", JSON.stringify(map));
      // Only file uploads use axios; keep it out of the entry chunk.
      //@ts-ignore
      const { axios: adapterAxios } = await import("@bundled-es-modules/axios");
      const axios = adapterAxios.create({
        withCredentials: true,
      });
      const { data } = await axios.post(this.$vuefront.baseURL, fd);

      if (!data.errors) {
        commit("vuefront/setError", false, { root: true });
        commit("setData", data);
      } else {
        commit("vuefront/setError", data.errors, {
          root: true,
        });
      }
    } catch (e) {
      commit("vuefront/setResponseError", e, {
        root: true,
      });
    }
  },
};
